import { GuidePage } from "@/components/pages/GuidePageTh";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import type { ServiceId } from "@/data/services";
import { speakableSchema, BUILD_DATE } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { BookOpen, Clock, Calendar, ChevronRight, ExternalLink } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const SOCIAL_IMG = "https://nyclanguageinstitute.com/social/nyc-legal-og.webp";

export const Route = createFileRoute("/guide/$slug")({
  loader: async ({ params }) => {
    const { getGuide, wordCount } = await import("@/data/guides");
    const g = getGuide(params.slug);
    if (!g) throw notFound();
    return { slug: params.slug, g, words: wordCount(g) };
  },
  head: ({ loaderData }: any) => {
    const g: any = loaderData?.g;
    if (!g) return { meta: [{ title: ctaTitle("ไม่พบคู่มือ", "/guide/$slug", "th") }] };
    const url = `${SITE}/guide/${g.slug}`;
    return {
      meta: [
        { title: ctaTitle(g.title, "/guide/$slug", "th") },
        { name: "description", content: ctaDesc(g.metaDesc, "/guide/$slug", "th") },
        { name: "keywords", content: g.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(g.title, "/guide/$slug", "th") },
        { property: "og:description", content: ctaDesc(g.metaDesc, "/guide/$slug", "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "th_TH" },
        { property: "article:published_time", content: g.updated },
        { property: "article:section", content: g.category },
        { property: "og:image", content: SOCIAL_IMG },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: g.title },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(g.title, "/guide/$slug", "th") },
        { name: "twitter:description", content: g.metaDesc },
        { name: "twitter:image", content: SOCIAL_IMG },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/guide/${g.slug}`)],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "@id": `${url}#article`,
              url,
              headline: g.h1,
              alternativeHeadline: g.title,
              description: g.metaDesc,
              image: [SOCIAL_IMG],
              abstract: g.tldr,
              wordCount: loaderData?.words ?? 0,
              datePublished: g.updated,
              dateModified: BUILD_DATE,
              inLanguage: "th",
              isAccessibleForFree: true,
              articleSection: g.category,
              keywords: g.keywords.join(", "),
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              isPartOf: { "@id": `${SITE}/#website` },
              about: { "@id": `${SITE}/#org` },
              author: { "@id": `${SITE}/#org` },
              publisher: { "@id": `${SITE}/#org` },
              citation: g.citations.map((c: any) => ({
                "@type": "CreativeWork",
                name: c.name,
                url: c.url,
              })),
              aggregateRating: aggregateRatingNode(`guide:${g.slug}`),
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: g.howTo.name,
              description: g.howTo.description,
              inLanguage: "th",
              ...(g.howTo.totalTime ? { totalTime: g.howTo.totalTime } : {}),
              estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: "700-5000" },
              supply: [
                { "@type": "HowToSupply", name: "Original document" },
                { "@type": "HowToSupply", name: "Passport / ID copy" },
              ],
              tool: [{ "@type": "HowToTool", name: "NYC Legal Notary Service" }],
              step: g.howTo.steps.map((s: any, i: number) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: s.name,
                text: s.text,
                url: `${url}#howto-step-${i + 1}`,
                ...(s.duration ? { performTime: s.duration } : {}),
              })),
              about: { "@id": `${SITE}/#org` },
              publisher: { "@id": `${SITE}/#org` },
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "th",
              isPartOf: { "@id": `${SITE}/#website` },
              about: { "@id": `${SITE}/#org` },
              mainEntity: g.faqs.map((f: any, i: number) => ({
                "@type": "Question",
                "@id": `${url}#faq-${i + 1}`,
                name: f.q,
                answerCount: 1,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: f.a,
                  inLanguage: "th",
                  url: `${url}#faq-${i + 1}`,
                  author: { "@id": `${SITE}/#org` },
                  dateModified: BUILD_DATE,
                },
              })),
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/guides` },
                { "@type": "ListItem", position: 3, name: g.h1, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: GuidePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบคู่มือนี้</h1>
      <Link to="/guides" className="mt-4 inline-block text-gold underline">
        ดูคู่มือทั้งหมด
      </Link>
    </main>
  ),
});
